import React from 'react';
import { useAPIContext } from '../../context/api';
import { VideoAPI } from '../../../../constructs/video-processing/interface';
import { PosterAPI } from '../../../../constructs/poster-processing/interface';

const sanitizeFieldKey = (fieldKey: string): string => {
  return fieldKey.replace(/[^a-zA-Z0-9_-]/g, '-');
};

export type UploadFileFn = (
  type: 'video',
  ext: string,
  file: File,
  clientId: string,
  bannerId: string,
  fieldKey: string,
  isManualUpload?: boolean
) => Promise<{ publicUrl: string }>;

export type UploadPosterFn = (
  type: 'image',
  ext: string,
  file: File,
  fileName: string,
  clientId: string,
  isManualUpload?: boolean
) => Promise<{ fileKey: string; fileName: string; path: string; publicUrl: string }>;

const buildPublicUrl = (fileKey: string): string => {
  // exlcude "optimized/"
  const publicPath = fileKey.split('/').slice(1).join('/');
  return `${process.env.REACT_APP_MEDIA_DOMAIN}/${publicPath}`;
};

export const useUploadMedia = () => {
  const { callEndpoint } = useAPIContext();

  const uploadVideoDirect: UploadFileFn = React.useCallback(
    async (fileType, fileExtension, file, clientId, bannerId, fieldKey, isManualUpload = true) => {
      try {
        if (!file || !file.type) {
          throw new Error('Archivo no válido o tipo de archivo no especificado.');
        }

        if (!['.mp4', '.webm'].includes(fileExtension.toLowerCase())) {
          throw new Error('Solo se permiten archivos MP4 y WebM para brands.');
        }

        const sanitizedFieldKey = sanitizeFieldKey(fieldKey);
        const fileName = `${bannerId}--${sanitizedFieldKey}`;
        const mode = 'direct';
        const path = `/video/${mode}/${fileName}`;
        const optionalPrefix = isManualUpload ? 'brands/manual-uploads' : 'brands';

        console.log(
          'Hook | Subiendo video con path:',
          path,
          'y optionalPrefix:',
          optionalPrefix,
          'y fileName:',
          fileName
        );

        const response = await callEndpoint<VideoAPI, 'post'>('upload', path, 'POST', {
          fileType,
          fileExtension,
          optionalPrefix
        });

        console.log('Hook | Respuesta del endpoint de upload:', response);
        const s3Response = await fetch(response.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });

        if (!s3Response.ok) {
          const errorText = await s3Response.text();
          throw new Error(`Error subiendo archivo a S3: ${s3Response.status} - ${errorText}`);
        }

        return {
          publicUrl: buildPublicUrl(response.fileKey)
        };
      } catch (error) {
        console.error('Hook | Error al subir el video:', error);
        throw error;
      }
    },
    [callEndpoint]
  );

  const uploadPosterDirect: UploadPosterFn = React.useCallback(
    async (fileType, fileExtension, file, fileName, clientId, isManualUpload = true) => {
      try {
        if (!file || !file.type) {
          throw new Error('Archivo no válido o tipo de archivo no especificado.');
        }

        // Validar que solo sea jpg/png en el frontend
        if (!['.jpg', '.jpeg', '.png'].includes(fileExtension.toLowerCase())) {
          throw new Error('Solo se permiten archivos JPG y PNG para brands.');
        }

        const mode = 'direct';
        const path = `/${mode}/${fileName}`;
        const optionalPrefix = isManualUpload ? 'brands/manual-uploads' : 'brands';

        const response = await callEndpoint<PosterAPI, 'post'>('upload', path, 'POST', {
          fileType,
          fileExtension,
          optionalPrefix
        });

        const { uploadUrl, fileKey } = response;

        const s3Response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });

        if (!s3Response.ok) {
          const errorText = await s3Response.text();
          throw new Error(`Error subiendo archivo a S3: ${s3Response.status} - ${errorText}`);
        }

        return {
          fileKey,
          fileName,
          path,
          publicUrl: buildPublicUrl(fileKey)
        };
      } catch (error) {
        console.error('Hook | Error al subir el poster:', error);
        throw error;
      }
    },
    [callEndpoint]
  );

  return { uploadVideoDirect, uploadPosterDirect };
};
